import type React from "react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { KnowledgeApi } from "~/functions/api/knowledge";
import { useProjectPermission } from "~/hooks/useProjectPermission";
import CloseIcon from "~/images/close.svg?react";
import type { Knowledge } from "~/interface/knowledge";
import type { RootState } from "~/store";
import styles from "./KnowledgeSidebar.module.scss";

interface KnowledgeSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  documentType: Knowledge["type"];
}

const KnowledgeSidebar: React.FC<KnowledgeSidebarProps> = ({ isOpen, onClose, documentType }) => {
  const navigate = useNavigate();
  const { organizationId, projectId } = useParams();
  const { currentUser, currentProject } = useSelector((state: RootState) => state.auth) as any;
  const { canViewKnowledge } = useProjectPermission();
  const [activeKnowledges, setActiveKnowledges] = useState<Knowledge[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  // ドキュメントタイプのラベルを取得
  const getDocumentTypeLabel = (type: Knowledge["type"]): string => {
    const typeMap: { [key: string]: string } = {
      "system-proposal": "システム概要書",
      "functional-requirements": "機能要件書",
      "non-functional-requirements": "非機能要件書",
      "plugin-proposal": "プラグイン提案書",
      "app-design": "アプリ設計書",
      "field-design": "フィールド設計書",
      "customize-specification": "カスタマイズ仕様書",
    };
    return typeMap[type] || type;
  };

  // 有効化中のナレッジを取得
  useEffect(() => {
    const fetchActiveKnowledges = async () => {
      if (!isOpen || !currentProject?.id || !currentUser?.organization_id) return;

      setIsLoading(true);
      try {
        const knowledgeApi = new KnowledgeApi();
        const data = await knowledgeApi.getByStatus(
          currentProject.id,
          currentUser.organization_id,
          "approved",
        );

        const dataArray = data ? (Array.isArray(data) ? data : [data]) : [];
        // フィルタリング: 有効化されている && 該当するドキュメントタイプ
        const filtered = dataArray.filter((k) => k.isActive && k.type === documentType);
        setActiveKnowledges(filtered);
      } catch (error) {
        console.error("Failed to fetch active knowledges:", error);
        setActiveKnowledges([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchActiveKnowledges();
  }, [isOpen, documentType, currentProject?.id, currentUser?.organization_id]);

  // ナレッジページへ遷移
  const handleGoToKnowledge = () => {
    if (organizationId && projectId) {
      navigate(`/${organizationId}/project/${projectId}/knowledge?type=${documentType}`);
    }
  };

  // プロンプトを省略表示
  const truncatePrompt = (prompt: string, maxLength: number = 100) => {
    if (!prompt) return "";
    return prompt.length > maxLength ? `${prompt.substring(0, maxLength)}...` : prompt;
  };

  if (!isOpen) return null;

  // 権限チェック: guestは表示不可
  if (!canViewKnowledge()) {
    return null;
  }

  return (
    <div className={styles["sidebar"]}>
      {/* ヘッダー */}
      <div className={styles["header"]}>
        <div className={styles["headerContent"]}>
          <div className={styles["headerTitle"]}>
            <h2>{getDocumentTypeLabel(documentType)}</h2>
          </div>
          <button className={styles["closeButton"]} onClick={onClose} aria-label="閉じる">
            <CloseIcon />
          </button>
        </div>
        <p className={styles["subtitle"]}>有効化中のナレッジ一覧</p>
      </div>

      {/* コンテンツ */}
      <div className={styles["content"]}>
        {isLoading ? (
          <div className={styles["loading"]}>読み込み中...</div>
        ) : activeKnowledges.length > 0 ? (
          <div className={styles["knowledgeList"]}>
            {activeKnowledges.map((knowledge) => (
              <div key={knowledge.id} className={styles["knowledgeCard"]}>
                <div className={styles["cardHeader"]}>
                  <h3 className={styles["cardTitle"]}>{knowledge.title || "無題"}</h3>
                  <span className={styles["activeBadge"]}>有効</span>
                </div>
                <p className={styles["cardPrompt"]}>{truncatePrompt(knowledge.meta_prompt)}</p>
                <div className={styles["cardMeta"]}>
                  <span className={styles["author"]}>
                    作成者: {knowledge.modified_by || "不明"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles["emptyState"]}>
            <p>有効化されているナレッジはありません</p>
          </div>
        )}
      </div>

      {/* フッター */}
      <div className={styles["footer"]}>
        <button className={styles["detailButton"]} onClick={handleGoToKnowledge}>
          詳細を見る →
        </button>
      </div>
    </div>
  );
};

export default KnowledgeSidebar;
